import React, { useState, useEffect } from "react";
import { API_BASE_URL } from "../utils/apiConfig";
import {
  Mail,
  AlertCircle,
  CheckCircle,
  Loader,
  X,
} from "lucide-react";

const TwoFactorLogin = ({
  userEmail,
  userId,
  twoFAType,
  onSuccess,
  onBack,
  onError,
  setSuccess
}) => {
  const [otpCode, setOtpCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [resendTimer, setResendTimer] = useState(0);

  // Start resend timer
  const startResendTimer = () => {
    setResendTimer(60);
  };

  // Resend timer effect
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(resendTimer => resendTimer - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // OTP input handlers
  const handleOTPInputChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    const newOtpCode = [...otpCode];
    newOtpCode[index] = value;
    setOtpCode(newOtpCode);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`otp-${index + 1}`);
      if (nextInput) nextInput.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otpCode[index] && index > 0) {
      const prevInput = document.getElementById(`otp-${index - 1}`);
      if (prevInput) prevInput.focus();
    }
  };

  // Generate 2FA code for EMAIL/PHONE types
  const generate2FACode = async () => {
    if (twoFAType === "GOOGLE_AUTH") return; // Google Auth doesn't need code generation

    setLoading(true);
    setError("");
    setMessage("");

    try {
      const response = await fetch(`${API_BASE_URL}/auth/generate-2fa-code`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId, // Send userId for login flow
        }),
      });

      const data = await response.json();
      if (response.ok) {
        setMessage(
          data.message || `Code sent to your ${twoFAType.toLowerCase()}`
        );
        startResendTimer();
      } else {
        setError(data.message || "Failed to send verification code");
      }
    } catch (err) {
      setError("Network error while sending verification code");
    } finally {
      setLoading(false);
    }
  };

  // Verify 2FA code
  const verify2FACode = async (e) => {
    e.preventDefault();

    const otpString = otpCode.join('');
    if (otpString.length !== 6) {
      setError("Please enter the complete 6-digit verification code");
      return;
    }

    setLoading(true);
    setError("");
    setMessage("");

    try {
      // For login flow, we use the same verify-2fa endpoint but include userId
      const response = await fetch(`${API_BASE_URL}/auth/verify-2fa`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          code: otpString,
          userId, // Include userId for login verification
          loginFlow: true, // Flag to indicate this is login flow
        }),
      });

      const data = await response.json();
      if (response.ok) {
        onSuccess(data);
      } else {
        setError(data.message || "Invalid verification code");
      }
    } catch (err) {
      setError("Network error while verifying code");
      onError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl p-8 w-full max-w-md shadow-xl">
        {/* Modal Header */}
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Mail className="w-5 h-5 text-blue-600" />
          </div>
          <h2 className="text-xl font-semibold text-slate-800">
            Google Auth
          </h2>
        </div>

        {/* Modal Content */}
        <p className="text-sm text-slate-600 mb-6">
          A temporary login has been sent to you Google Auth
        </p>

        {/* OTP Input Fields */}
        <div className="mb-9">
          <div className="flex justify-center space-x-2">
            {otpCode.map((digit, index) => (
              <div key={index} className="flex space-x-1">
                <input
                  id={`otp-${index}`}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOTPInputChange(index, e.target.value)}
                  onKeyDown={(e) => handleOTPKeyDown(index, e)}
                  className="w-12 h-12 text-center text-lg font-semibold border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                  style={{ backgroundColor: "#F6F7F8" }}
                />
                {index === 2 && <span className="text-slate-400 text-xl self-center">-</span>}
              </div>
            ))}
          </div>
        </div>

        {/* Resend Timer */}
        <div className="flex  items-center mb-4">
          <div className="text-sm text-slate-600 text-center">
            Can't find email? Check spam folder or contact{" "}
            <a href="mailto:support@3pa-y.com" className="text-blue-600 hover:underline">
              support@3pa-y.com
            </a>

          </div>
          {/* <button
            onClick={generate2FACode}
            disabled={resendTimer > 0}
            className={`text-sm font-medium ${
              resendTimer > 0 
                ? "text-slate-400 cursor-not-allowed" 
                : "text-blue-600 hover:text-blue-700"
            }`}
          >
            {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend"}
          </button> */}

        </div>

        {/* <div className="text-sm text-slate-600 text-center mt-1 mb-3">

          <button
            onClick={generate2FACode}
            // disabled={resendTimer > 0}
            className={`text-sm font-medium ${resendTimer > 0
                ? "text-slate-400 cursor-not-allowed"
                : "text-blue-600 hover:text-blue-700"
              }`}
          >
            {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend Code"}
          </button>
        </div> */}
        {/* Error Message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
            <span className="text-red-700 text-sm">{error}</span>
            <button
              onClick={() => setError("")}
              className="ml-auto text-red-500 hover:text-red-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Success Message */}
        {message && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
            <span className="text-green-700 text-sm">{message}</span>
            <button
              onClick={() => setMessage("")}
              className="ml-auto text-green-500 hover:text-green-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-slate-200 mb-4"></div>

        {/* Continue Button */}
        <form onSubmit={verify2FACode}>
          <button
            type="submit"
            disabled={loading || otpCode.join('').length !== 6}
            className="w-full text-white py-3 px-4 rounded-lg font-medium transition-all duration-300 flex items-center justify-center space-x-2 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              background: "linear-gradient(180deg, #3A5AD1 0%, #31ABFF 100%)",
              borderRadius: "15px"
            }}
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              "Continue"
            )}
          </button>
        </form>
        <div className="text-sm text-slate-600 text-center mt-4">
          <button
            onClick={onBack}
            className="text-blue-600 hover:underline"
            disabled={loading}
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorLogin;
